import BaseListPresenter from "../../../../base/BaseListPresenter";
import { getCurrentUserUseCase } from "../../../../usecases/user";
import {
  format,
  startOfDay,
  addHours,
  addMinutes,
  addDays,
  parseISO,
} from "date-fns";
import xhr from "../../../../usecases/xhr/Xhr";

class TotalSalesWeekDayPresenter extends BaseListPresenter {
  init() {
    this.store = [];
    this.limit = 50000;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.filterDate = {};
    this.filterStore = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  componentDidMount() {
    this.init();
    this.findStore();

    return this.getObjects();
  }

  componentWillUnmount() {
    this.cleanup();
  }

  async getObjects() {
    this.reset();
    // await this.countObjects();
    this.find();
  }

  async findStore() {
    const user = await getCurrentUserUseCase().execute();
    const storeQuery = { include: ["all"], where: { email: user.email } };
    this.findObjectUseCase.abort();
    this.store = await this.findObjectUseCase.execute("users", storeQuery);

    const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
    );
    this.view.setStore(store);
  }

  async countObjects() {
    try {
      this.showProgress();
      const collection = this.view.getCollectionName();
      this.objects = [];
      const query = this.createQuery({ skip: 0, limit: this.limit });
      delete query.limit;
      delete query.include;

      const startDate = query.where?.tran_date?.$gte || "";
      const endDate = query.where?.tran_date?.$lte || "";

      const chunkQuery = {
        ...query,
        where: {
          ...query.where,
          tran_date: {
            $gte: startDate,
            $lte: endDate,
          },
        },
      };

      console.log("COUNT QUERY2:", query);

      const { count } = await this.countObjectUseCase.execute(
        collection,
        chunkQuery,
      );
      this.count = count;
      this.view.setCount(this.count);
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
  }

  async find() {
    try {
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort();
        console.log("Aborted previous requests");
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.abortController = new AbortController();
      this.objects = [];
      const startTime = Date.now();

      const query = this.createQuery({ skip: 0, limit: this.limit });
      delete query.limit;
      delete query.include;

      const startDate = query.where?.tran_date?.$gte || "";
      const endDate = query.where?.tran_date?.$lte || "";

      console.log("COUNT QUERY1:", query);

      const dateRanges = this.generateDateRanges(startDate, endDate);

      for (let i = 0; i < dateRanges.length; i++) {
        const range = dateRanges[i];

        const chunkQuery = {
          ...query,
          where: {
            ...query.where,
            tran_date: {
              $gte: range.start,
              $lte: range.end,
            },
          },
        };

        try {
          const chunkData = await xhr.get("/transactions", {
            params: chunkQuery,
            signal: this.abortController.signal,
          });

          this.objects = this.objects.concat(chunkData);
          this.view.setObjects(this.objects);
        } catch (chunkError) {
          if (chunkError.name === "AbortError") {
            console.log("Request was aborted at chunk", i + 1);
            return;
          }
          console.error(`Error fetching ${range.label}:`, chunkError.message);
        }
      }

      const downloadTime = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(
        `✅ Complete! ${this.objects.length.toLocaleString()} records in ${downloadTime}s`,
      );
      this.abortController = null;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Entire operation was aborted");
        return;
      }
      console.error("Error:", error.message);
    }
  }

  cleanup() {
    if (this.abortController) {
      console.log("🧹 Cleaning up...");
      this.abortController.abort();
      this.abortController = null;
    }
    this.objects = [];
  }

  // Simple range generator: ≤31 days = 1 request, >31 days = monthly chunks
  generateDateRanges(startDateStr, endDateStr) {
    const start = new Date(startDateStr.split(" ")[0]);
    const end = new Date(endDateStr.split(" ")[0]);

    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    console.log(`📆 Date range: ${daysDiff} days`);

    // If 31 days or less, single request
    if (daysDiff <= 31) {
      console.log("Strategy: Single request (≤31 days)");
      return [
        {
          start: this.formatDate(start),
          end: this.formatDate(end),
          label: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
        },
      ];
    }

    // More than 31 days, use monthly chunks
    console.log("Strategy: Monthly chunks (>31 days)");
    return this.generateMonthlyRanges(start, end);
  }

  // Generate monthly ranges
  generateMonthlyRanges(startDate, endDate) {
    const ranges = [];
    let current = new Date(startDate);

    while (current < endDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
        23,
        59,
        59,
      );

      if (monthEnd > endDate) {
        monthEnd.setTime(endDate.getTime());
      }

      ranges.push({
        start: this.formatDate(monthStart),
        end: this.formatDate(monthEnd),
        label: monthStart.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });

      current.setMonth(current.getMonth() + 1);
    }

    return ranges;
  }

  // Format date for API
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 05:00:00.000`;
  }

  createQuery({ skip = 0, limit = this.limit } = {}) {
    const storeCodes =
      this.store?.flatMap((s) => s.storeAnalytic?.map((a) => a?.name)) ?? [];

    const escapedSearch = this.searchAny
      ?.trim()
      ?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const whereClause = {
      ...(this.where || {}),
      ...(this.filter || {}),
      ...(this.filterDate || {}),
    };

    if (this.filterStore === "AllStore") {
      if (storeCodes.length > 0) {
        whereClause.store_code = { $in: storeCodes };
      }
    } else if (this.filterStore) {
      whereClause.store_code = this.filterStore;
    }

    // 3) Build final query
    const query = {
      limit,
      skip,
      where: whereClause,
      include: this.include,
      keys: this.keys ?? [
        "store_code",
        "tran_date",
        "total",
        "total_taxfree",
        "total_tax",
        "discount_amount",
        "createdAt",
        "cust_name",
        "tran_type",
        "paymenttype",
        "cust_count",
        "total_pretax",
        "status",
      ],
      sort: this.sort,
    };

    return query;
  }

  // async findObjects() {
  //   // 1) Load user + store (kailangan bago gumawa ng whereClause)
  //   try {
  //     const user = await getCurrentUserUseCase().execute();
  //     const storeQuery = { include: ["all"], where: { email: user.email } };
  //     this.store = await this.findObjectUseCase.execute("users", storeQuery);

  //     const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
  //       a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
  //     );
  //     this.view.setStore(store);
  //   } catch (error) {
  //     console.error("Error fetching user or store data:", error);
  //     return;
  //   }

  //   // 2) Build base filters (AllStore -> $in ng lahat ng analytic names)
  //   const storeCodes =
  //     this.store?.[0]?.storeAnalytic?.map((a) => a?.name).filter(Boolean) ?? [];

  //   const whereClause = {
  //     ...this.filterDate,
  //     store_code:
  //       this.filterStore === "AllStore"
  //         ? { $in: storeCodes }
  //         : this.filterStore,
  //   };

  //   // fixed: true batching size
  //   const pageSize = this.limit || 50000;

  //   // 3) Count muna gamit ang full filter
  //   let totalCount = 0;
  //   try {
  //     this.showProgress();
  //     this.findObjectUseCase.abort(); // cancel any in-flight

  //     const { count } = await this.countObjectUseCase.execute(
  //       this.view.getCollectionName(),
  //       { where: whereClause },
  //     );

  //     totalCount = count ?? 0;
  //     this.count = totalCount;
  //     this.view.setCount(totalCount);

  //     // Reset UI state
  //     this.objects = [];
  //     this.view.setObjects([]);
  //     this.view.setTotal(0);
  //     this.view.setNotFound(false);

  //     // Not-found delay (optional)
  //     let timeoutId = null;
  //     if (totalCount === 0) {
  //       timeoutId = setTimeout(() => {
  //         if (this.objects.length === 0) {
  //           console.log("No data after delay. Setting NotFound = true");
  //           this.view.setNotFound(true);
  //         }
  //       }, 30000);
  //       // Walang data — wala nang ipe-fetch na pages
  //       if (timeoutId) clearTimeout(timeoutId);
  //       return;
  //     }

  //     // 4) Batch pagination loop
  //     let fetched = 0;
  //     let page = 0;

  //     // Shared buffer + periodic flush (para smooth ang UI)
  //     let buffer = [];
  //     const flushBuffer = () => {
  //       if (buffer.length === 0) return;
  //       this.objects = this.objects.concat(buffer);
  //       this.view.setObjects(this.objects);
  //       this.view.setTotal(this.objects.length);
  //       buffer = [];
  //     };
  //     const flushInterval = setInterval(flushBuffer, 1000);

  //     try {
  //       while (fetched < totalCount) {
  //         const skip = page * pageSize;

  //         const query = {
  //           skip,
  //           limit: pageSize, // <<— important: 20 per batch
  //           where: whereClause,
  //           include: ["all"],
  //           sort: this.sort,
  //           keys: [
  //             "store_code",
  //             "tran_date",
  //             "total",
  //             "total_taxfree",
  //             "total_tax",
  //             "discount_amount",
  //             "createdAt",
  //             "cust_name",
  //             "tran_type",
  //             "paymenttype",
  //             "cust_count",
  //             "total_pretax",
  //             "status",
  //           ],
  //         };

  //         // Per-page local buffer via onData
  //         let pageRowCount = 0;
  //         const onData = (objects) => {
  //           if (objects?.length) {
  //             buffer.push(...objects);
  //             pageRowCount += objects.length;
  //           }
  //         };

  //         // Execute one page (may stream sa loob, pero hanggang `limit` lang)
  //         await this.findObjectUseCase.execute(
  //           this.view.getCollectionName(),
  //           query,
  //           { timeout: 120000, onData },
  //         );

  //         // Push whatever came in this page
  //         flushBuffer();

  //         // Walang dumating sa page na ito → tapos na
  //         if (pageRowCount === 0) break;

  //         fetched += pageRowCount;
  //         page += 1;

  //         // Yield para hindi ma-block UI thread
  //         await Promise.resolve();
  //       }
  //     } finally {
  //       clearInterval(flushInterval);
  //       flushBuffer();
  //     }

  //     // Safeguard
  //     if (this.objects.length === 0) {
  //       this.view.setNotFound(true);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching objects:", error);
  //     this.view.showError(error);
  //   } finally {
  //     this.hideProgress();
  //     this.progress = false;
  //   }
  // }

  onChangeStore(where) {
    this.filterStore = where;
    this.getObjects();
  }

  onChangeDate(where) {
    const { tran_date } = where;
    console.log("tran", tran_date);

    if (tran_date && tran_date.$gte && tran_date.$lte) {
      const startDate = addHours(startOfDay(parseISO(tran_date.$gte)), 5);
      const endDate = addMinutes(
        addHours(addDays(startOfDay(parseISO(tran_date.$lte)), 0), 4),
        59,
      );

      const formattedStartDate = format(startDate, "yyyy-MM-dd HH:mm:ss.SSS");
      const formattedEndDate = format(endDate, "yyyy-MM-dd HH:mm:ss.SSS");

      this.filterDate = {
        tran_date: {
          $gte: formattedStartDate,
          $lte: formattedEndDate,
        },
      };
    }

    console.log("date", this.filterDate);
    this.getObjects();
  }
}

export default TotalSalesWeekDayPresenter;
